import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ColorPalette } from "@/components/color-palette/ColorPalette";
import { Icon } from "@/components/icons/Icon";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";
import { setFontSize } from "@/components/tiptap/utils/editorActions";

import { CollapsedPopover } from "../CollapsedPopover";

import { FontSizeDropdown } from "./FontSizeDropdown";
import { InlineTextStyleDropdown } from "./inline-text-style/InlineTextStyleDropdown";
import { InlineCodeButton } from "./InlineCodeButton";
import { LinkButton } from "./LinkButton";
import { LinkEditInput } from "./LinkEditInput";

import type { DocumentModel } from "@/models/DocumentModel";
import type { Color, FontSize } from "@corca/data";

const Content = observer(({ doc }: { doc: DocumentModel }) => {
  const t = useTranslations("sidebar");
  const { tiptap, editorState } = doc.editor;

  if (!tiptap) return null;

  const handleFontSizeChange = (fontSize: FontSize) => {
    setFontSize(doc, fontSize);
  };

  const handleColorChange = (color: Color) => {
    tiptap.chain().focus().setColor(color).run();
  };
  return (
    <>
      <SidebarToolbar label={t("toolbars.textFormat")} className="flex-1 justify-between">
        {!editorState.isExpression && (
          <FontSizeDropdown fontSize={editorState.textSize} onChange={handleFontSizeChange} />
        )}
        {!editorState.isExpression && <InlineTextStyleDropdown doc={doc} />}
        {!editorState.isExpression && (
          <ColorPalette
            tooltipTitle={t("textStyle.textColor")}
            currentColor={editorState.textColor || "black"}
            onSelect={handleColorChange}
            hintKey="textColor"
          />
        )}
        {!editorState.isExpression && <InlineCodeButton doc={doc} />}
        <LinkButton doc={doc} />
      </SidebarToolbar>
      <LinkEditInput doc={doc} />
    </>
  );
});

export const InlineTextStyle = observer(function InlineTextStyle({
  doc,
  isCollapsed,
}: {
  doc: DocumentModel;
  isCollapsed?: boolean;
}) {
  const { tiptap } = doc.editor;
  const t = useTranslations();

  if (!tiptap) {
    return null;
  }

  if (isCollapsed) {
    return (
      <CollapsedPopover
        icon={<Icon name="TextStyle" />}
        tooltipTitle={t("sidebar.textStyle.title")}
        title={t("sidebar.textStyle.title")}
      >
        <Content doc={doc} />
      </CollapsedPopover>
    );
  }

  return <Content doc={doc} />;
});
